import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { RouterModule } from '@angular/router';
import { AccessConstants } from '../../../../core/constants/access-constants';
import { Contact } from '../../../../core/models/civi-crm/contact/contact';
import { PhoneNumberService } from '../../../../core/services/phone-number/phone-number.service';
import { PostalCodeService } from '../../../../core/services/postal-code/postal-code.service';
import { ResourcesService } from '../../../../core/services/resources/resources.service';
import { CardsOnFileComponent } from '../cards-on-file/cards-on-file.component';
import { UserProfileResponse } from '../shared/models/user-profile-response';
import { UserStateService } from '../../shared/services/user-state/user-state.service';
import { UserProfileService } from '../shared/services/user-profile/user-profile.service';
import { SpectrumAdvisorInfoComponent } from '../spectrum-advisor-info/spectrum-advisor-info.component';
import { ViewExecutorProfile } from './models/view-executor-profile';
import { UserAvailabilityComponent } from '../user-availability/user-availability.component';

@Component({
    selector: 'app-user-view',
    imports: [
        RouterModule,
        MatButtonModule,
        SpectrumAdvisorInfoComponent,
        CardsOnFileComponent,
        UserAvailabilityComponent,
    ],
    templateUrl: './view-executor-profile.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./view-executor-profile.component.scss'],
})
export class ViewExecutorProfileComponent implements OnInit {
    profile: ViewExecutorProfile | undefined;
    advisorContact: Contact | null = null;

    get canAccessPayments(): boolean {
        const role = this.userStateService.getRole();
        return (
            role !== undefined &&
            AccessConstants.RolesThatCanAccessPayments.includes(role)
        );
    }

    get canManageAvailability(): boolean {
        const role = this.userStateService.getRole();
        return (
            role !== undefined &&
            AccessConstants.RolesThatCanManageUserAvailability.includes(role)
        );
    }

    get userId(): number | undefined {
        return this.userStateService.getUser()?.id;
    }

    constructor(
        private readonly resourcesService: ResourcesService,
        private readonly userProfileService: UserProfileService,
        private readonly userStateService: UserStateService,
        private readonly phoneNumberService: PhoneNumberService,
        private readonly postalCodeService: PostalCodeService,
    ) {}

    async ngOnInit(): Promise<void> {
        this.userProfileService
            .get()
            .then(async (profileResponse: UserProfileResponse) => {
                this.profile = await this.transformProfileData(profileResponse);
                this.advisorContact = profileResponse.advisorContact;
            });
    }

    private async transformProfileData(
        profileResponse: UserProfileResponse,
    ): Promise<ViewExecutorProfile> {
        return {
            username: profileResponse.user.username,
            fullName: `${profileResponse.contact.firstName} ${profileResponse.contact.lastName}`,
            email: profileResponse.contact.email.emailAddress,
            organizationName: profileResponse.organization.name,
            jobTitle: profileResponse.contact.jobTitle,
            streetAddress: profileResponse.contact.address.streetAddress,
            city: profileResponse.contact.address.city,
            stateProvince: await this.resourcesService.getStateProvince(
                profileResponse.contact.address.stateProvince,
            ),
            postalCode: this.postalCodeService.buildPostalCodeString(
                profileResponse.contact.address.postalCode,
                profileResponse.contact.address.postalCodeSuffix,
            ),
            phoneNumber: this.phoneNumberService.buildPhoneNumberString(
                profileResponse.contact.phone.phoneNumber,
                profileResponse.contact.phone.phoneExtension,
            ),
        };
    }
}
